import { computed, ref } from 'vue'

import IndexDatabaseService from '@/core/services/idb/IndexDatabaseService'
import SelectedAccountDataEntitiesService from '@/core/services/cachedData/SelectedAccountDataEntitiesService'
import LanguageCachedDataService from '@/core/services/cachedData/LanguageCachedDataService'
import DashboardCachedDataService from '@/core/services/cachedData/DashboardCachedDataService'
import TagsCachedDataService from '@/core/services/cachedData/TagsCachedDataService'
import TemplatesCategoryCachedService from '@/core/services/cachedData/TemplatesCategoryCachedService'
import TemplatesDictionariesCachedService from '@/core/services/cachedData/TemplatesDictionariesCachedService'
import EntitiesDataService from '@/core/services/cachedData/EntitiesDataService'
import { getCurrentTokenId } from '@/composables/useTabSpecificStorage'
import Logger from '@/core/services/Logger'


/**
 * Object with Global Services
 */
const globalServices = {
    EntitiesDataService,
    DashboardCachedDataService,
    LanguageCachedDataService,
    TagsCachedDataService,
    TemplatesCategoryCachedService,
    TemplatesDictionariesCachedService
} as const

const logger = Logger.makeSectionLogger('CACHED_DATA_SERVICE')

const globalServiceKeys = Object.keys(globalServices)

export type GlobalServicesType = typeof globalServices
export type GlobalServicesKeys = keyof GlobalServicesType
export type GlobalServicesValues = GlobalServicesType[keyof GlobalServicesType]
type OnDataRefreshedCallback = (...args: Array<unknown>) => unknown

/**
 * Type guard to check if a string is a valid global service key
 * Checks if the key exists as a property in the globalServices object
 *
 * Example: isGlobalServiceKey("DashboardCachedDataService") → true
 *          isGlobalServiceKey("SomeRandomKey") → false
 */
function isGlobalServiceKey (key: string): key is GlobalServicesKeys {
    return key in globalServices
}

/**
 * Class GlobalDataService
 *
 * Load / Clear Global Data and set to cache (IndexDB)
 */
class GlobalCachedDataService {
    private onAllDataRefreshedCallbacks: Array<OnDataRefreshedCallback> = []
    public loadedDataCount = ref(0)
    public loadedFromAPIData = ref<Array<GlobalServicesKeys>>([])
    public isAllAPICallsFulfilled = computed(() => {
        return globalServiceKeys.length === this.loadedFromAPIData.value.length
    })

    private markServiceAsLoaded (key: GlobalServicesKeys) {
        if (this.loadedFromAPIData.value.includes(key)) return

        this.loadedFromAPIData.value.push(key)
    }

    private triggerOnAllDataRefreshedCallbacks () {
        this.onAllDataRefreshedCallbacks.forEach(cb => cb())

        this.onAllDataRefreshedCallbacks = []
    }

    public onAllDataRefreshed (cb: OnDataRefreshedCallback) {
        this.onAllDataRefreshedCallbacks.push(cb)
    }

    getServiceByKey (key: GlobalServicesKeys) {
        return globalServices[key]
    }

    public async updateServiceData <T extends GlobalServicesKeys> (key: T, data: GlobalServicesType[T]['data']) {
        this.getServiceByKey(key).data = data

        await IndexDatabaseService.setObjectToStore({
            key: key,
            data,
            tokenId: getCurrentTokenId()
        })
    }

    async loadServiceData (key: GlobalServicesKeys) {
        const data = await this.getServiceByKey(key).loadData()

        this.markServiceAsLoaded(key)

        if (globalServiceKeys.length === this.loadedFromAPIData.value.length) {
            this.triggerOnAllDataRefreshedCallbacks()
        }

        await IndexDatabaseService.setObjectToStore({
            key: key,
            data,
            tokenId: getCurrentTokenId()
        })
    }

    async loadSelectedAccountEntitiesOnly (accountId: number) {
        const data = await SelectedAccountDataEntitiesService.loadData(accountId)
        await IndexDatabaseService.setObjectToStore({
            key: 'SelectedAccountDataEntitiesService',
            data,
            tokenId: getCurrentTokenId()
        })
    }

    /**
     * Loads data for all global services.
     *
     * - If cached data exists for a service AND belongs to the current token:
     *   - It sets the service's data to the cached value immediately.
     *   - Initiates an asynchronous refresh of the data without waiting for it to complete.
     * - If no cached data is found OR cache belongs to a different token:
     *   - It loads the data, updates both the service and the cache.
     *   - Waits for the data loading operation to complete before proceeding.
     *
     * @returns {Promise<void[]>} A promise that resolves when all required data loading operations are complete.
     *                            Each promise corresponds to a service data load operation where data was not cached.
     */
    async loadAllData () {
        const cacheData = await IndexDatabaseService.getDatabaseStore()
        const currentTokenId = getCurrentTokenId()

        const serviceDataLoadStep = 1 / globalServiceKeys.length

        return Promise.all(
            globalServiceKeys
                .map(
                    async (key) => {
                        const _key = key as GlobalServicesKeys

                        logger.log('Will decide how to load data for:', _key)
                        const serviceInCache = cacheData.find(i => i.key === _key)

                        // Check if cache exists AND belongs to current token
                        const isCacheRelevant = serviceInCache && serviceInCache.tokenId === currentTokenId

                        if (!isCacheRelevant) {
                            if (serviceInCache && serviceInCache.tokenId !== currentTokenId) {
                                logger.log('Cache belongs to different token, will load fresh data')
                            } else {
                                logger.log('Data is not in cache, will wait for it to load')
                            }
                            await this.loadServiceData(_key)
                        } else {
                            // Set the cached data to the service immediately
                            this.getServiceByKey(_key).data = serviceInCache.data

                            logger.log('Restored data from cache (same token), doing asynchronous refresh')

                            this.loadServiceData(_key)
                        }

                        this.loadedDataCount.value += serviceDataLoadStep
                    }
                )
        )
            .then(() => {
                this.loadedDataCount.value = 1
            })
    }

    async clearAllData () {
        const globalEntries = Object.values(globalServices)

        globalEntries.forEach(service => {
            service.clearData()
        })

        // Clear all global service cache keys from IndexedDB
        // This ensures token/version changes clear cached data
        const allCachedData = await IndexDatabaseService.getDatabaseStore()
        const cacheKeys = allCachedData
            .map(item => item.key)
            .filter(key => isGlobalServiceKey(key))

        if (cacheKeys.length > 0) {
            logger.log(`Clearing ${cacheKeys.length} cache keys from IndexedDB`)
            await Promise.all(
                cacheKeys.map(key => IndexDatabaseService.removeObjectFromStore(key))
            )
        }
    }
}

export default new GlobalCachedDataService()
